import { AccountRow, ResultRow, SystemConfig, SystemStyles } from './types';
import { Helpers } from './helpers';

// cleanNumber defaults blank cells to 0 for legacy controls. That is not evidence of zero IB.
const openingBalanceMetadata = (value: unknown): { ibAvailable?: boolean } => {
    const known = typeof value === 'number' ? Number.isFinite(value)
        : typeof value === 'string' && /^[+-]?\d[\d\s.,'\u00a0]*-?$/.test(value.trim())
            && Number.isFinite(Helpers.cleanNumber(value));
    return known ? {} : { ibAvailable: false };
};

// --- MAPA KOLORÓW PER SYSTEM (Tailwind wymaga pełnych klas, nie dynamicznych) ---
export const SYSTEM_STYLES: Record<string, SystemStyles> = {
    '24seven': {
        hoverBorder: 'hover:border-pink-500',
        iconBg: 'bg-pink-100 dark:bg-pink-900',
        iconText: 'text-pink-600 dark:text-pink-300',
        titleText: 'text-pink-600',
    },
    'fiken': {
        hoverBorder: 'hover:border-blue-500',
        iconBg: 'bg-blue-100 dark:bg-blue-900',
        iconText: 'text-blue-600 dark:text-blue-300',
        titleText: 'text-blue-600',
    },
    'tripletex': {
        hoverBorder: 'hover:border-emerald-500',
        iconBg: 'bg-emerald-100 dark:bg-emerald-900',
        iconText: 'text-emerald-600 dark:text-emerald-300',
        titleText: 'text-emerald-600',
    },
    'unimicro': {
        hoverBorder: 'hover:border-violet-500',
        iconBg: 'bg-violet-100 dark:bg-violet-900',
        iconText: 'text-violet-600 dark:text-violet-300',
        titleText: 'text-violet-600',
    },
};

// --- LOGIKA SYSTEMÓW (PARSERY) ---
export const SYSTEMS: Record<string, SystemConfig> = {
    '24seven': {
        name: 'Finago',
        color: 'pink',
        costsArePositive: true,
        validate: (content: string): boolean => content.includes('Kontonr') && (content.includes('Totalt') || content.includes('IB')),
        parseSaldo: (rows: any[][]): AccountRow[] => {
            return rows.filter(r => r.length >= 5 && r[0] && !isNaN(parseInt(r[0]))).map(r => {
                const accNum = parseInt(r[0].toString().substring(0, 4));
                if (isNaN(accNum)) return null;
                return {
                    id: Helpers.cleanAccountID(r[0]),
                    accNum,
                    name: Helpers.cleanText(r[1]),
                    ib: Helpers.cleanNumber(r[2]),
                    change: Helpers.cleanNumber(r[3]),
                    ...openingBalanceMetadata(r[2]),
                    ub: Helpers.cleanNumber(r[4])
                };
            }).filter(Boolean) as AccountRow[];
        },
        parseResultat: (headers: string[], rows: any[][]): ResultRow[] => {
            const validRows = rows.filter(r => r[0] && r[0].toString().trim() !== '');
            const totaltIdx = headers.findIndex(h => h.toLowerCase() === 'totalt');
            if (totaltIdx === -1) return []; // brak kolumny Totalt → nie parsuj
            return validRows.map(r => {
                const monthsData: Record<string, number> = {};
                for (let i = 2; i < totaltIdx; i++) {
                    const mName = Helpers.translateMonth(headers[i]) || headers[i];
                    monthsData[mName] = Helpers.cleanNumber(r[i]);
                }
                const accNum = parseInt(r[0].toString().substring(0, 4));
                if (isNaN(accNum)) return null;
                return {
                    id: Helpers.cleanAccountID(r[0]),
                    accNum,
                    name: Helpers.cleanText(r[1]),
                    months: monthsData,
                    sum: Helpers.cleanNumber(r[totaltIdx])
                };
            }).filter(Boolean) as ResultRow[];
        }
    },
    'fiken': {
        name: 'Fiken',
        color: 'blue',
        costsArePositive: false,
        validate: (content: string): boolean => {
            const s = content.slice(0, 1000).toLowerCase();
            return s.includes('fiken') || (s.includes('konto') && s.includes('endring')) || s.includes('driftsinntekter');
        },
        parseSaldo: (rows: any[][]): AccountRow[] => {
            const dataStartIdx = rows.findIndex(r => r.join(';').includes('Inngående') && r.join(';').includes('Utgående')) + 1;
            const cleanRows = rows.slice(dataStartIdx);
            return cleanRows.filter(r => r.length >= 5 && r[0]).map(r => {
                const rawId = Helpers.cleanAccountID(r[0]);
                if (!/^\d+/.test(rawId)) return null;
                return {
                    id: rawId,
                    accNum: parseInt(rawId.substring(0, 4)),
                    name: Helpers.cleanText(r[1]),
                    ib: Helpers.cleanNumber(r[2]),
                    change: Helpers.cleanNumber(r[3]),
                    ...openingBalanceMetadata(r[2]),
                    ub: Helpers.cleanNumber(r[4])
                };
            }).filter(Boolean) as AccountRow[];
        },
        parseResultat: (headers: string[], rows: any[][]): ResultRow[] => {
            const idxNr = headers.findIndex(h => h.includes('Driftsinntekter'));
            const idxSum = headers.findIndex(h => h.includes('Denne perioden'));
            const monthCols: { idx: number; name: string }[] = [];
            headers.forEach((h, i) => {
                if (i !== idxNr && i !== idxNr + 1 && i !== idxSum && h.trim() !== '') {
                    monthCols.push({ idx: i, name: Helpers.translateMonth(h) });
                }
            });
            return rows.filter(r => r[idxNr] && !isNaN(parseInt(r[idxNr]))).map(r => {
                const monthsData: Record<string, number> = {};
                monthCols.forEach(m => monthsData[m.name] = Helpers.cleanNumber(r[m.idx]));
                return {
                    id: Helpers.cleanAccountID(r[idxNr]),
                    accNum: parseInt(r[idxNr].toString().substring(0, 4)),
                    name: Helpers.cleanText(r[idxNr + 1]),
                    months: monthsData,
                    sum: Helpers.cleanNumber(r[idxSum])
                };
            });
        }
    },
    'tripletex': {
        name: 'Tripletex',
        color: 'emerald',
        costsArePositive: true,
        validate: (content: string): boolean => content.includes('Kontonummer') && (content.includes('Utgående saldo') || content.includes('t.o.m')),
        parseSaldo: (rows: any[][]): AccountRow[] => {
            return rows.filter(r => r.length >= 5 && r[0] && !isNaN(parseInt(r[0]))).map(r => ({
                id: Helpers.cleanAccountID(r[0]),
                accNum: parseInt(r[0].toString().substring(0, 4)),
                name: Helpers.cleanText(r[1]),
                ib: Helpers.cleanNumber(r[2]),
                change: Helpers.cleanNumber(r[3]),
                ...openingBalanceMetadata(r[2]),
                ub: Helpers.cleanNumber(r[4])
            }));
        },
        parseResultat: (headers: string[], rows: any[][]): ResultRow[] => {
            let sumIdx = -1;
            for (let i = 2; i < headers.length; i++) {
                const h = headers[i].toLowerCase();
                if (h.includes('t.o.m') || h.match(/^\d{4}$/) || h.match(/\d{4}.*?(\d{4})/)) {
                    sumIdx = i;
                    break;
                }
            }
            if (sumIdx === -1) sumIdx = headers.length - 1;
            return rows.filter(r => r[0] && !isNaN(parseInt(r[0]))).map(r => {
                const monthsData: Record<string, number> = {};
                for (let i = 2; i < sumIdx; i++) {
                    const mName = Helpers.translateMonth(headers[i]);
                    monthsData[mName] = Helpers.cleanNumber(r[i]);
                }
                return {
                    id: Helpers.cleanAccountID(r[0]),
                    accNum: parseInt(r[0].toString().substring(0, 4)),
                    name: Helpers.cleanText(r[1]),
                    months: monthsData,
                    sum: Helpers.cleanNumber(r[sumIdx])
                };
            });
        }
    },
    'unimicro': {
        name: 'Uni Micro',
        color: 'violet',
        costsArePositive: false,
        validate: (content: string): boolean => content.includes('Uni Micro') || content.includes('Side') || content.includes('Årstall') || content.includes('PHText'),
        parseSaldo: (rows: any[][]): AccountRow[] => {
            return rows.filter(r => r[0] && r[0].toString().trim().match(/^\d{4}$/)).map(r => {
                const debet = Helpers.cleanNumber(r[3]);
                const kredit = Helpers.cleanNumber(r[4]);
                const change = debet - kredit;
                return {
                    id: Helpers.cleanAccountID(r[0]),
                    accNum: parseInt(r[0].toString().substring(0, 4)),
                    name: Helpers.cleanText(r[1]),
                    ib: Helpers.cleanNumber(r[2]),
                    change: change,
                    ...openingBalanceMetadata(r[2]),
                    ub: Helpers.cleanNumber(r[r.length - 1]),
                    rawDebet: debet,
                    rawKredit: kredit
                };
            });
        },
        parseResultat: (headers: string[], rows: any[][]): ResultRow[] => {
            const sumIdx = headers.findIndex(h => h.toLowerCase() === 'sum');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
            return rows.filter(r => r[0] && r[0].toString().trim().match(/^\d{4}$/)).map(r => {
                const monthsData: Record<string, number> = {};
                months.forEach(m => {
                    const idx = headers.findIndex(h => h.toLowerCase() === m.toLowerCase());
                    if (idx > -1) monthsData[m] = Helpers.cleanNumber(r[idx]);
                });
                return {
                    id: Helpers.cleanAccountID(r[0]),
                    accNum: parseInt(r[0].toString().substring(0, 4)),
                    name: Helpers.cleanText(r[1]),
                    months: monthsData,
                    sum: Helpers.cleanNumber(r[sumIdx > -1 ? sumIdx : r.length - 1])
                };
            });
        }
    }
};

// Expose to window for backward compatibility
(window as any).SYSTEM_STYLES = SYSTEM_STYLES;
(window as any).SYSTEMS = SYSTEMS;
